// The malbox mark: an isometric box drawn in dots with one enlarged specimen dot (--mark-accent).
//   <Mark className scan? />   size and colour come from the class: "brand-mark" (nav, 26px) or "lockup-mark" (Submit, 56–84px)
//       scan (brand-mark only): one dot column at a time turns live cyan, left to right, while any run is live. Stepped from
//       JS about 14 times a second (a fill can't be composited, so a per-frame CSS animation on 74 dots repainted every
//       frame on every page); paused while the tab is hidden, static under reduced motion.
// The static mark SVG is the only dangerouslySetInnerHTML in the app (never event data).
import { useEffect, useRef } from "react";
import markSvg from "@/assets/mark.svg?raw";
import { cx } from "./cx";

const STEP_MS = 70;
const CYCLE_MS = 3600;

export function Mark({ className, scan }: { className?: string; scan?: boolean }) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!scan || !el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    // columns by x, left to right (the specimen dot keeps its own colour)
    const byX = new Map<number, SVGCircleElement[]>();
    for (const c of el.querySelectorAll<SVGCircleElement>("circle:not(.mk-accent)")) {
      const x = Math.round(Number(c.getAttribute("cx")) * 10);
      const col = byX.get(x);
      if (col) col.push(c);
      else byX.set(x, [c]);
    }
    const cols = [...byX.keys()].sort((a, b) => a - b).map((x) => byX.get(x) ?? []);
    let i = 0;
    let timer = 0;
    const paint = (n: number, on: boolean) => cols[n]?.forEach((c) => (c.style.fill = on ? "var(--live)" : ""));
    const tick = () => {
      if (document.hidden) return; // resumed by visibilitychange
      paint(i - 1, false);
      if (i < cols.length) {
        paint(i, true);
        i++;
        timer = window.setTimeout(tick, STEP_MS);
      } else {
        i = 0;
        timer = window.setTimeout(tick, Math.max(STEP_MS, CYCLE_MS - cols.length * STEP_MS));
      }
    };
    const onVisible = () => {
      window.clearTimeout(timer);
      if (!document.hidden) tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    tick();
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
      cols.forEach((_, n) => paint(n, false));
    };
  }, [scan]);
  return <span ref={ref} className={cx("inline-block", className)} data-scan={scan ? "true" : undefined} aria-hidden="true" dangerouslySetInnerHTML={{ __html: markSvg }} />;
}
